import AgentExplainerLayout, { Section, PointList } from "@/components/marketing/AgentExplainerLayout";
import { AgentHeroVisual, HybridCallFlow } from "@/components/marketing/AgentFlowDiagram";
import { Link } from "react-router-dom";

export default function HybridAgents() {
  return (
    <AgentExplainerLayout
      active="hybrid"
      testId="hybrid-agents-page"
      eyebrow="Agents · Hybrid"
      title="Hybrid Agents: Voice for volume, AI for value, humans for closes."
      lead={(n) =>
        `Hybrid is not a third product—it is a campaign design pattern in ${n}. Start cheap with Voice Agents, escalate interested callers into AI Agents, and warm-transfer only the deals that need a person.`
      }
      heroVisual={<AgentHeroVisual variant="hybrid" />}
    >
      <Section eyebrow="What they are" title="One call, three intelligence layers" testId="hybrid-what">
        <div className="grid gap-8 lg:grid-cols-2 lg:items-center">
          <div className="space-y-4 text-muted-foreground">
            <p>
              Most dials should never burn a Live AI minute. Hybrid flows open with Voice prompts—
              identity, consent, simple yes/no—then hand the conversation to an AI Agent only when
              the caller shows intent. Humans enter last, already briefed by captured fields.
            </p>
            <PointList
              items={[
                { title: "Voice first", body: "Kill voicemail, wrong numbers, and tire-kickers cheaply." },
                { title: "AI second", body: "Qualify, answer product questions, book or soft-close." },
                { title: "Human last", body: "SIP transfer with name, email, and context headers." },
              ]}
            />
          </div>
          <div className="rounded-2xl border border-border bg-card p-5 shadow-lg">
            <p className="font-mono text-[11px] uppercase tracking-wider text-primary">Funnel math</p>
            <div className="mt-4 space-y-3">
              {[
                ["100 dials answered", "Voice handles all"],
                ["~35 show interest", "AI engages"],
                ["~8 ready to buy / need help", "Human transfer"],
              ].map(([a, b], i) => (
                <div key={a} className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{a}</p>
                    <p className="text-xs text-muted-foreground">{b}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-muted-foreground">
              Ratios vary by list quality—the architecture stays the same.
            </p>
          </div>
        </div>
      </Section>

      <Section eyebrow="Call flow" title="How Voice and AI share one campaign" testId="hybrid-flow">
        <p className="mb-6 max-w-2xl text-muted-foreground">
          Designers wire edges between Voice nodes and AI nodes the same way they wire any other branch—Hybrid is the pattern, not a special runtime.
        </p>
        <HybridCallFlow />
        <div className="mt-8 overflow-hidden rounded-2xl border border-border">
          <div className="grid sm:grid-cols-4">
            {[
              { t: "Start · Voice", d: "Greeting + consent + “press 1 if interested”" },
              { t: "Filter", d: "No / silence / voicemail → hangup path" },
              { t: "AI Agent", d: "Live qualify, KB answers, book or collect" },
              { t: "Transfer", d: "Human SIP with captured CRM fields" },
            ].map((c, i) => (
              <div
                key={c.t}
                className={`border-border p-4 ${i < 3 ? "border-b sm:border-b-0 sm:border-r" : ""} bg-card`}
              >
                <p className="font-mono text-[10px] text-primary">Step {i + 1}</p>
                <p className="disp mt-1 text-sm font-semibold">{c.t}</p>
                <p className="mt-1 text-xs text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section eyebrow="Benefits" title="Why Hybrid usually beats “AI everywhere”" testId="hybrid-benefits">
        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              t: "Lower blended cost",
              b: "Most minutes stay on Voice / TTS. Live AI only runs when the lead earns it.",
            },
            {
              t: "Higher AI hit-rate",
              b: "The model talks to warmer people, so fewer empty greetings and fewer wasted sessions.",
            },
            {
              t: "Clearer ops",
              b: "Voice failures and AI failures show up on different nodes—easier to tune.",
            },
            {
              t: "Human morale",
              b: "Reps get transferred conversations that already passed a filter, not raw cold dials.",
            },
          ].map((x) => (
            <div key={x.t} className="rounded-2xl border border-border bg-card p-5">
              <p className="disp font-semibold">{x.t}</p>
              <p className="mt-2 text-sm text-muted-foreground">{x.b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="Design tips" title="Build Hybrid well" testId="hybrid-tips">
        <div className="grid gap-6 lg:grid-cols-2">
          <PointList
            items={[
              { title: "Keep Voice short", body: "Ten seconds of clarity beats a two-minute IVR novel." },
              { title: "One clear escalate key", body: "“Press 1 to talk” or a spoken yes should be unmistakable." },
              { title: "Pass context forward", body: "Collect name/intent in Voice or early AI so transfers are warm." },
              { title: "Always allow continue on AI", body: "Exit-only action sets make Live leave before it hears the caller." },
            ]}
          />
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
            <p className="disp font-semibold">Related reading</p>
            <ul className="mt-3 space-y-2 text-sm">
              <li>
                <Link to="/agents/voice" className="font-semibold text-primary hover:underline">
                  Voice Agents
                </Link>
                <span className="text-muted-foreground"> — cheap scripted layer</span>
              </li>
              <li>
                <Link to="/agents/ai" className="font-semibold text-primary hover:underline">
                  AI Agents
                </Link>
                <span className="text-muted-foreground"> — live conversation layer</span>
              </li>
              <li>
                <Link to="/features" className="font-semibold text-primary hover:underline">
                  Features
                </Link>
                <span className="text-muted-foreground"> — flow designer & engines</span>
              </li>
            </ul>
          </div>
        </div>
      </Section>
    </AgentExplainerLayout>
  );
}
